import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { DownloadSimple, ChartBar, FileXls } from "@phosphor-icons/react";

function firstDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function lastDayOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

export default function SalesReportPage() {
  const [from, setFrom] = useState(firstDayOfMonth(new Date(new Date().setMonth(new Date().getMonth() - 2))));
  const [to, setTo] = useState(lastDayOfMonth());
  const [groupBy, setGroupBy] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/sales", { params: { from, to, group_by: groupBy } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupBy]);

  const downloadCsv = async () => {
    try {
      const res = await fetch(`${API}/reports/sales.csv?from=${from}&to=${to}&group_by=${groupBy}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sales-${groupBy}-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadXlsx = async () => {
    try {
      const res = await fetch(`${API}/reports/sales.xlsx?from=${from}&to=${to}&group_by=${groupBy}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sales-${groupBy}-${from}-to-${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const maxTotal = useMemo(() => {
    if (!data?.rows?.length) return 1;
    return Math.max(...data.rows.map(r => r.total || 0), 1);
  }, [data]);

  return (
    <div data-testid="sales-report-page">
      <PageHeader
        title="Sales Report"
        subtitle="Tax + Repair invoices grouped by day / month / customer / product. Download CSV for Excel or Tally."
      />

      <div className="p-8 max-w-6xl space-y-6">
        <div className="card-base p-5 flex items-end gap-4 flex-wrap">
          <div><div className="label-overline block mb-1.5">From</div><input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="sr-from" /></div>
          <div><div className="label-overline block mb-1.5">To</div><input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="sr-to" /></div>
          <div>
            <div className="label-overline block mb-1.5">Group By</div>
            <select className="field" value={groupBy} onChange={e => setGroupBy(e.target.value)} data-testid="sr-group">
              <option value="invoice">Invoice-wise</option>
              <option value="day">Date (daily)</option>
              <option value="month">Month</option>
              <option value="customer">Customer</option>
              <option value="product">Product</option>
              <option value="doc_type">Document Type</option>
            </select>
          </div>
          <button onClick={load} disabled={loading} className="btn-primary inline-flex items-center gap-1.5" data-testid="sr-run"><ChartBar size={14} weight="bold" /> {loading ? "Running…" : "Run Report"}</button>
          <button onClick={downloadCsv} disabled={!data} className="btn-secondary inline-flex items-center gap-1.5" data-testid="sr-csv"><DownloadSimple size={14} weight="bold" /> Download CSV</button>
          <button onClick={downloadXlsx} disabled={!data} className="btn-secondary inline-flex items-center gap-1.5" data-testid="sr-xlsx"><FileXls size={14} weight="bold" /> Download Excel</button>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard label="Records" value={data.total.count} />
              <KpiCard label="Taxable" value={fmtINR(data.total.taxable)} />
              <KpiCard label="Tax" value={fmtINR(data.total.tax)} />
              <KpiCard label="Total" value={fmtINR(data.total.total)} accent="bg-brand text-white" />
            </div>

            <div className="card-base p-0 overflow-hidden">
              <div className="table-wrapper border-0 rounded-none">
                <table className="table-base">
                  <thead><tr>
                    {groupBy === "invoice" && <th>Date</th>}
                    <th>{groupBy === "invoice" ? "Invoice No" : groupBy === "customer" ? "Customer" : groupBy === "product" ? "Product" : groupBy === "doc_type" ? "Doc Type" : (groupBy === "day" ? "Date" : "Month")}</th>
                    {groupBy === "invoice" && <th>Customer</th>}
                    {groupBy === "product" && <th className="text-right">Qty</th>}
                    {groupBy !== "invoice" && <th className="text-right">Count</th>}
                    <th className="text-right">Taxable</th>
                    <th className="text-right">Tax</th>
                    <th className="text-right">Total</th>
                    <th>Share</th>
                  </tr></thead>
                  <tbody>
                    {data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-ink-muted">No sales in this period.</td></tr>}
                    {data.rows.map((r, i) => {
                      const pct = Math.round((r.total / maxTotal) * 100);
                      return (
                        <tr key={`${r.key}-${i}`} data-testid={`sr-row-${i}`}>
                          {groupBy === "invoice" && <td className="tabular text-xs">{r.date}</td>}
                          <td className="font-semibold">{r.key}</td>
                          {groupBy === "invoice" && <td>{r.customer}</td>}
                          {groupBy === "product" && <td className="text-right tabular">{Number(r.qty || 0).toFixed(2)}</td>}
                          {groupBy !== "invoice" && <td className="text-right tabular">{r.count}</td>}
                          <td className="text-right tabular">{fmtINR(r.taxable)}</td>
                          <td className="text-right tabular">{fmtINR(r.tax)}</td>
                          <td className="text-right tabular font-semibold">{fmtINR(r.total)}</td>
                          <td><div className="w-32 bg-surface-muted rounded-full h-1.5 overflow-hidden"><div className="bg-brand h-full" style={{ width: `${pct}%` }} /></div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
      <FieldStyle />
    </div>
  );
}

function KpiCard({ label, value, accent }) {
  return (
    <div className={`card-base p-4 ${accent || ""}`}>
      <div className={`label-overline mb-1 ${accent ? "text-white/70" : ""}`}>{label}</div>
      <div className={`font-display text-2xl font-bold tabular ${accent ? "text-white" : "text-ink"}`}>{value}</div>
    </div>
  );
}
